import { Box3, Euler, Group, Matrix4, Vector3 } from "three";
import * as Globals from "./globals";
import * as LZString from "lz-string";
import {
    buildingGroup,
    furnitureGroup,
    highlightsGroup,
    measurementsGroup,
    rebindGroup,
    roomGroup,
    roomNamesGroup,
    syncShellMenu,
    tmpGroup,
} from "./main";
import * as SceneManager from "./sceneManager";
import {
    clearGroup,
    clearScene,
    render,
    scene,
    sceneCamera,
} from "./sceneManager";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter";
import { findChildrenByName, findElementByUUID } from "./utils/threeUtils";
import { generateMeasurements } from "./models/measurements/measurementGeneration";
import { Text } from "./models/baseElements/text";
import { createRooms } from "./models/rooms/roomGeneration";
import {
    closeSpinner,
    openSpinner,
    showErrorMessage,
    showToast,
} from "../ui/ui";
import { t } from "../i18n";
import { reconstructUISettings } from "./utils/dialogUtils";
import { recalculateBuildingParameters } from "./calculators/buildingParameters/parameterCalculator";
import { controlObjectName } from "./constants/nameConstants";
import {
    fitPerspectiveCameraToObject,
    zoomOrthographicCameraToBounds,
} from "./utils/cameraUtils";
import { updateWallSnapAxis } from "./models/walls/wallGeneration";
import { exportLayoutView, exportSceneView } from "./output/outputGeneration";
import {
    currentTotalPrice,
    recalculatePrice,
} from "./calculators/buildingParameters/costCalculator";
import { parseToFixedFloat, toFixedFloat } from "./utils/utils";
import { calculateVolumes } from "./calculators/buildingParameters/volumeCalculator";
import { recalculateBuildTime } from "./calculators/buildingParameters/buildTimeCalculator";
import { getFloorArea } from "./models/floor/floorGeneration";
import { getRoof } from "./models/roofs/roofGeneration";

//Save current scene to file.
export function saveProject(callback = null, errorCallback = null) {
    /*TODO
       Lichter werden nicht exportiert: checken,
       Materialien nicht alle standard or basic material > checken, (linebasicmaterial
       Möbelstücke auslagern nur verweise speichern
    */

    let scene = SceneManager.scene;

    if (
        buildingGroup.children.length === 0 &&
        furnitureGroup.children.length === 0
    ) {
        showErrorMessage(t("project_empty"));
        if (errorCallback !== null) {
            errorCallback();
        }
        return;
    }

    //Prüfen ob ein Name vergeben wurde.
    let name = $("#current-project-name-input").attr("value");
    if (name.length === 0) {
        showErrorMessage(t("project_name_required"));
        if (errorCallback !== null) {
            errorCallback();
        }
        return;
    }

    openSpinner(t("project_saving"));
    clearAdditionalSceneContent();
    scene.updateMatrixWorld();
    let v = calculateVolumes();
    recalculatePrice(v).then();
    recalculateBuildTime(v.total.unCutVolume, v.total.cutVolume).then();
    let image = exportSceneView();
    let layoutImage = exportLayoutView();
    let sceneJson = scene.toJSON();
    let boundsSize = new Box3()
        .setFromObject(buildingGroup)
        .getSize(new Vector3());
    const exporter = new GLTFExporter();
    let totalArea = 0;
    if (roomGroup.children.length > 0) {
        roomGroup.children.forEach((child) => {
            let area = child.userData.area;
            if (area !== null) {
                totalArea += parseToFixedFloat(area);
            }
        });
    }

    let roof = getRoof();
    let roofThickness = roof ? roof.userData.roof.thickness : 0;
    let roofOverhang = roof ? roof.userData.roof.overhang : 0;

    exporter.parse(
        scene, // called when the gltf has been generated
        function (gltf) {
            let gltfJson = JSON.stringify(gltf);
            //console.log('glttfJson', gltfJson);
            gltf = LZString.compressToBase64(gltfJson);
            sceneJson = LZString.compressToBase64(JSON.stringify(sceneJson));

            let id = templateId === "new" ? "" : "/" + templateId;
            $.ajax({
                headers: {
                    "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr(
                        "content",
                    ),
                },
                method: "POST",
                url: window.location.origin + "/configurator/scene" + id,
                data: {
                    name: name,
                    gltf: gltf,
                    json: sceneJson,
                    vo: LZString.compressToBase64(JSON.stringify(v)),
                    image: image,
                    layoutImage: layoutImage,
                    public: "false",
                    material_cost:
                        currentTotalPrice === null
                            ? 0
                            : toFixedFloat(currentTotalPrice, 2),
                    width: toFixedFloat(boundsSize.x, 3),
                    depth: toFixedFloat(boundsSize.z, 3),
                    height: toFixedFloat(boundsSize.y, 3),
                    roof_thickness: toFixedFloat(roofThickness, 3),
                    roof_overhang: toFixedFloat(roofOverhang, 3),
                    floorArea: parseToFixedFloat(getFloorArea()),
                    area: isNaN(totalArea) ? 0 : toFixedFloat(totalArea, 3),
                    roomCount: roomGroup.children.length,
                },
            })
                .done(function (data) {
                    if (data.error !== undefined) {
                        showErrorMessage(data.error);
                        if (errorCallback !== null) {
                            errorCallback();
                        }
                    } else if (data.success !== undefined && data.success) {
                        showToast(t("project_saved"));
                        templateId = data.id;
                        setSceneId(templateId);

                        closeSpinner();
                        if (errorCallback !== null) {
                            errorCallback();
                        }
                    }
                })
                .fail(function (xhr, status, error) {
                    if (error !== undefined) {
                        showErrorMessage(
                            t("project_save_failed", { error: error }),
                        );
                    }
                    if (errorCallback !== null) {
                        errorCallback();
                    }
                    closeSpinner();
                })
                .always(() => {
                    restoreAdditionalSceneContent(callback);
                });
        }, // called when there is an error in the generation
        function (error) {
            console.error("Fehler beim Projekt speichern: " + error);
            restoreAdditionalSceneContent();
            closeSpinner();
            if (errorCallback !== null) {
                errorCallback();
            }
        },
        {
            forceIndices: true,
            includeCustomExtensions: true,
            onlyVisible: false,
        },
    );
}

//save-silence for auto-save
export function saveSilently() {
    /*TODO
       Lichter werden nicht exportiert: checken,
       Materialien nicht alle standard or basic material > checken, (linebasicmaterial
       Möbelstücke auslagern nur verweise speichern
    */

    let scene = SceneManager.scene;

    if (
        buildingGroup.children.length === 0 &&
        furnitureGroup.children.length === 0
    ) {
        return;
    }

    //Prüfen ob ein Name vergeben wurde.
    let name = $("#current-project-name-input").attr("value");
    if (name.length === 0) {
        return;
    }

    scene.updateMatrixWorld();
    let v = calculateVolumes();
    recalculatePrice(v).then();
    recalculateBuildTime(v.total.unCutVolume, v.total.cutVolume).then();
    let image = exportSceneView();
    let layoutImage = exportLayoutView();
    let sceneJson = scene.toJSON();
    let boundsSize = new Box3()
        .setFromObject(buildingGroup)
        .getSize(new Vector3());
    const exporter = new GLTFExporter();
    let totalArea = 0;
    if (roomGroup.children.length > 0) {
        roomGroup.children.forEach((child) => {
            let area = child.userData.area;
            if (area !== null) {
                totalArea += parseToFixedFloat(area);
            }
        });
    }

    let roof = getRoof();
    let roofThickness = roof ? roof.userData.roof.thickness : 0;
    let roofOverhang = roof ? roof.userData.roof.overhang : 0;

    exporter.parse(
        scene, // called when the gltf has been generated
        function (gltf) {
            let gltfJson = JSON.stringify(gltf);
            //console.log('glttfJson', gltfJson);
            gltf = LZString.compressToBase64(gltfJson);
            sceneJson = LZString.compressToBase64(JSON.stringify(sceneJson));

            let id = templateId === "new" ? "" : "/" + templateId;
            $.ajax({
                headers: {
                    "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr(
                        "content",
                    ),
                },
                method: "POST",
                url: window.location.origin + "/configurator/scene" + id,
                data: {
                    name: name,
                    gltf: gltf,
                    json: sceneJson,
                    vo: LZString.compressToBase64(JSON.stringify(v)),
                    image: image,
                    layoutImage: layoutImage,
                    public: "false",
                    material_cost:
                        currentTotalPrice === null
                            ? 0
                            : toFixedFloat(currentTotalPrice, 2),
                    width: toFixedFloat(boundsSize.x, 3),
                    depth: toFixedFloat(boundsSize.z, 3),
                    height: toFixedFloat(boundsSize.y, 3),
                    roof_thickness: toFixedFloat(roofThickness, 3),
                    roof_overhang: toFixedFloat(roofOverhang, 3),
                    floorArea: parseToFixedFloat(getFloorArea()),
                    area: isNaN(totalArea) ? 0 : toFixedFloat(totalArea, 3),
                    roomCount: roomGroup.children.length,
                },
            })
                .done(function (data) {
                    if (data.error !== undefined) {
                        console.log(data.error);
                    } else if (data.success !== undefined && data.success) {
                        templateId = data.id;
                        setSceneId(templateId);
                    }
                })
                .fail(function (xhr, status, error) {
                    console.log(error);
                })
                .always(() => {
                    //restoreAdditionalSceneContent(callback);
                });
        }, // called when there is an error in the generation
        function (error) {
            console.error("Fehler beim Projekt speichern: " + error);
        },
        {
            forceIndices: true,
            includeCustomExtensions: true,
            onlyVisible: false,
        },
    );
}

function setSceneId(sceneId) {
    let stateObj = { id: sceneId };
    window.history.pushState(
        stateObj,
        "Scene saved",
        "/configurator?template_id=" + sceneId,
    );
}

//Open scene from file
export function openProject(id) {
    if (id === "new") {
        reconstructUISettings(null);
        return; //Leeres Projekt
    }
    openSpinner(t("project_loading"));
    let url = window.location.origin + "/configurator/scene/" + id;
    templateId = id;

    $.ajax({
        url: url,
    }).done(function (data) {
        if (!data.gltf) {
            closeSpinner();
            reconstructUISettings(null);
            return;
        }
        clearScene();

        const loader = new GLTFLoader();
        let actual_JSON = JSON.parse(LZString.decompressFromBase64(data.json));
        let gltfJSON = LZString.decompressFromBase64(data.gltf);

        loader.parse(
            gltfJSON,
            "",
            function (gltf) {
                for (let i = gltf.scene.children.length - 1; i >= 0; i--) {
                    let child = gltf.scene.children[i];
                    if (
                        child.name === "baseSceneGroup" ||
                        child.name === "highlightingGroup"
                    ) {
                        continue;
                    }

                    let originalJsonChild = findElementByUUID(
                        actual_JSON.object,
                        child.userData.uuid,
                    ); //Same child in manually exported json
                    let returnVal = reconstructScene(
                        scene,
                        child,
                        originalJsonChild,
                    );
                    if (returnVal != null) {
                        scene.addWithUuid(returnVal);
                    }
                }

                let controlObjects = findChildrenByName(
                    buildingGroup,
                    controlObjectName,
                    false,
                    true,
                );
                for (let i = 0; i < controlObjects.length; i++) {
                    controlObjects[i].userData.originalParent =
                        controlObjects[i].parent.uuid;
                }
                restoreAdditionalSceneContent();
                reconstructUISettings(null);
                let bounds = new Box3()
                    .setFromObject(buildingGroup)
                    .expandByObject(measurementsGroup);
                zoomOrthographicCameraToBounds(
                    SceneManager.layoutCamera,
                    bounds,
                    SceneManager.layoutCamera.aspect,
                    1.05,
                );
                fitPerspectiveCameraToObject(
                    sceneCamera,
                    SceneManager.sceneControls,
                    buildingGroup,
                    1.3,
                );
                render();
                recalculateBuildingParameters().then();
                closeSpinner();
                updateWallSnapAxis();
                syncShellMenu();
            }, // called when loading has errors
            function (error) {
                console.log("An error happened: \n" + error);
                closeSpinner();
            },
        );
    });
}

function reconstructScene(insertInto, gltfChild, sceneChild) {
    let gltfChildName = gltfChild.name.toString().toLowerCase();
    if (gltfChildName.includes("group")) {
        let group = new Group();
        group.name = sceneChild.name;
        group.layers.mask = sceneChild.layers;
        group.userData = sceneChild.userData;
        let oldMatrix = new Matrix4();
        oldMatrix.copy(gltfChild.matrix);
        group.applyMatrix4(oldMatrix);
        rebindGroup(group);
        if (
            gltfChild.hasOwnProperty("children") &&
            gltfChild.children.length > 0
        ) {
            reconstructChildren(group, gltfChild, sceneChild);
        }
        return group;
    } else if (
        gltfChild.hasOwnProperty("children") &&
        gltfChild.children.length > 0
    ) {
        if (sceneChild === null) {
            return gltfChild;
        }
        gltfChild.layers.mask = sceneChild.layers;
        gltfChild.userData = sceneChild.userData;
        gltfChild.name = sceneChild.name === undefined ? "" : sceneChild.name;
        reconstructChildren(gltfChild, gltfChild, sceneChild);
        return gltfChild;
    } else {
        if (gltfChild.name === "intersectPlane") {
            return null;
        }
        if (sceneChild != null) {
            gltfChild.userData = sceneChild.userData;
            gltfChild.layers.mask = sceneChild.layers;
            gltfChild.name =
                sceneChild.name === undefined ? "" : sceneChild.name;
        }
        return gltfChild;
    }
}

function reconstructChildren(insertInto, gltfChild, sceneChild) {
    for (let i = gltfChild.children.length - 1; i >= 0; i--) {
        let newGltfChild = gltfChild.children[i]; //Child added to scene
        let newSceneChild = findElementByUUID(
            sceneChild,
            newGltfChild.userData.uuid,
        ); //Same child in manually exported json

        if (newGltfChild.name === "intersectPlane") {
            continue;
        }

        let newObject = reconstructScene(
            insertInto,
            newGltfChild,
            newSceneChild,
        );
        if (newObject !== undefined && newSceneChild !== null) {
            newObject.userData = reconstructUserData(newSceneChild.userData);
            // console.log(newSceneChild.userData);
            newObject.userData.olduuid =
                newSceneChild.userData.olduuid || newSceneChild.uuid;

            newObject.name =
                newSceneChild.name === undefined ? "" : newSceneChild.name;
            insertInto.addWithUuid(newObject);
        }
    }
}

export function reconstructUserDataRecursive(object) {
    object.userData = reconstructUserData(object.userData);
    for (let i = 0; i < object.children.length; i++) {
        reconstructUserDataRecursive(object.children[i]);
    }
}

function reconstructUserData(savedUserData, depth = 0) {
    for (let key in savedUserData) {
        if (!savedUserData.hasOwnProperty(key)) {
            continue;
        }

        if (key === "originalGeometry") {
            continue;
        }

        if (key === "position" || key === "point") {
            savedUserData[key] = new Vector3(
                savedUserData[key].x,
                savedUserData[key].y,
                savedUserData[key].z,
            );
        } else if (key === "points") {
            for (let i = 0; i < savedUserData[key].length; i++) {
                savedUserData[key][i] = new Vector3(
                    savedUserData[key][i].x,
                    savedUserData[key][i].y,
                    savedUserData[key][i].z,
                );
            }
        } else if (key === "rotation") {
            savedUserData[key] = new Euler(
                savedUserData[key]._x,
                savedUserData[key]._y,
                savedUserData[key]._z,
                savedUserData[key]._order,
            );
        } else if (depth <= 5) {
            if (
                savedUserData[key] !== null &&
                typeof savedUserData[key] === "object"
            ) {
                if (
                    savedUserData[key].writable === undefined ||
                    (savedUserData[key].writable !== undefined &&
                        savedUserData[key].writable &&
                        key !== 0)
                ) {
                    savedUserData[key] = reconstructUserData(
                        savedUserData[key],
                        ++depth,
                    );
                }
            }
        }
    }

    return savedUserData;
}

function clearAdditionalSceneContent() {
    clearGroup(measurementsGroup);
    clearGroup(roomNamesGroup);
    clearGroup(highlightsGroup);
    clearGroup(tmpGroup);
}

function restoreAdditionalSceneContent(callback = null) {
    //Init bemaßungen
    generateMeasurements().then(() => {
        for (let i = 0; i < buildingGroup.children.length; i++) {
            let child = buildingGroup.children[i];
            if (child.name === "roomGroup") {
                let type = child.userData.roomType;
                let text = new Text(
                    type,
                    0.2,
                    "center",
                    -0.25,
                    new Vector3(0, 0, 0),
                    new Euler(-Math.PI / 2, 0, 0),
                    Globals.layers.roomNames,
                );
                child.addWithUuid(text);
            }
        }

        // if (roomGroup.children.length === 0) {
        createRooms();
        // }

        if (callback !== null) {
            setTimeout(() => {
                callback();
            }, 500);
        }
    });
}
